import { Head, Link } from "@inertiajs/react"
import { Plus, Search } from "lucide-react"
import { useMemo, useState } from "react"
import { AppLayout } from "../../components/layout/AppLayout"
import { buttonVariants } from "../../components/ui/button"
import { Input } from "../../components/ui/input"
import { cn } from "../../lib/cn"

type Company = {
  id: number
  name: string
  cnpj: string
  modo_emissao: "web" | "txt" | "web_e_txt" | string
  invoices_count: number
  created_at: string
}

type EmpresasIndexProps = {
  companies: Company[]
}

function modoLabel(value: string) {
  if (value === "web") return "Somente Web"
  if (value === "txt") return "Somente TXT"
  if (value === "web_e_txt") return "Web + TXT"
  return value
}

export default function EmpresasIndex({ companies }: EmpresasIndexProps) {
  const [search, setSearch] = useState("")

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return companies
    return companies.filter((company) => company.name.toLowerCase().includes(q) || company.cnpj.includes(q))
  }, [companies, search])

  return (
    <>
      <Head title="Empresas" />
      <AppLayout>
        <div className="p-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold tracking-tight text-white">Empresas</h1>
            <p className="text-slate-400">Gerencie credenciais Omie e modo de emissão por empresa.</p>
          </div>

          <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="relative w-full max-w-md">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input
                placeholder="Buscar por nome ou CNPJ..."
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                className="border-slate-700 bg-slate-900 pl-9 text-white placeholder:text-slate-500"
              />
            </div>
            <div className="flex items-center gap-3">
              <p className="text-sm text-slate-400">{filtered.length} empresa(s)</p>
              <Link href="/empresas/nova" className={buttonVariants()}>
                <Plus className="mr-2 h-4 w-4" />
                Nova Empresa
              </Link>
            </div>
          </div>

          <div className="overflow-x-auto rounded-lg border border-slate-800 bg-slate-900">
            <table className="min-w-full text-left text-sm">
              <thead className="border-b border-slate-800 bg-slate-900/70 text-slate-300">
                <tr>
                  <th className="px-4 py-3 font-medium">Nome</th>
                  <th className="px-4 py-3 font-medium">CNPJ</th>
                  <th className="px-4 py-3 font-medium">Modo de emissão</th>
                  <th className="px-4 py-3 text-center font-medium">Notas</th>
                  <th className="px-4 py-3 text-right font-medium">Ações</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td className="px-4 py-8 text-center text-slate-400" colSpan={5}>
                      Nenhuma empresa encontrada.
                    </td>
                  </tr>
                ) : (
                  filtered.map((company) => (
                    <tr key={company.id} className="border-b border-slate-800/80 last:border-b-0">
                      <td className="px-4 py-3 font-medium text-slate-100">{company.name}</td>
                      <td className="px-4 py-3 font-mono text-xs text-slate-300">{company.cnpj}</td>
                      <td className="px-4 py-3 text-slate-200">{modoLabel(company.modo_emissao)}</td>
                      <td className="px-4 py-3 text-center text-slate-200">{company.invoices_count}</td>
                      <td className="px-4 py-3 text-right">
                        <Link
                          href={`/empresas/${company.id}/editar`}
                          className={cn(buttonVariants({ variant: "outline" }), "border-slate-700 bg-transparent")}
                        >
                          Editar
                        </Link>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </AppLayout>
    </>
  )
}
